import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useConfirmEmailChange } from '@/api/hooks/useProfile';
import { LogoFull } from '@/components/LogoFull';
import { Button } from '@/components/ui/Button';

export function EmailChangeConfirmPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';

    const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>(
        token ? 'idle' : 'error'
    );
    const [errorMessage, setErrorMessage] = useState<string | null>(
        token ? null : 'Invalid or missing confirmation token.'
    );

    const confirmMutation = useConfirmEmailChange();

    function handleConfirm() {
        setStatus('loading');
        confirmMutation.mutate(token, {
            onSuccess: () => {
                setStatus('success');
            },
            onError: (err) => {
                setStatus('error');
                if (err instanceof Error) {
                    if (err.message.includes('expired')) {
                        setErrorMessage('This confirmation link has expired. Please request a new email change.');
                    } else if (err.message.includes('invalid') || err.message.includes('not found')) {
                        setErrorMessage('This confirmation link is invalid. Please request a new email change.');
                    } else {
                        setErrorMessage(err.message || 'Failed to confirm email change. Please try again.');
                    }
                } else {
                    setErrorMessage('An unexpected error occurred');
                }
            },
        });
    }

    if (status === 'idle') {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
                <div className="w-full max-w-md">
                    <div className="mb-8 text-center">
                        <Link to="/">
                            <LogoFull className="mx-auto h-10 w-auto" />
                        </Link>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                        <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                            Confirm Email Change
                        </h1>

                        <p className="mb-6 text-center text-sm text-gray-600 dark:text-gray-400">
                            Click the button below to confirm you want to change your email address.
                            After confirmation, we'll send a verification link to your new email.
                        </p>

                        <Button
                            type="button"
                            onClick={handleConfirm}
                            className="w-full"
                            size="lg"
                        >
                            Confirm Email Change
                        </Button>

                        <div className="mt-6 text-center">
                            <Link
                                to="/profile"
                                className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                                Cancel
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (status === 'loading') {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
                <div className="w-full max-w-md">
                    <div className="mb-8 text-center">
                        <Link to="/">
                            <LogoFull className="mx-auto h-10 w-auto" />
                        </Link>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                        <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                            Confirming email change...
                        </h1>
                        <div className="flex justify-center">
                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
                <div className="w-full max-w-md">
                    <div className="mb-8 text-center">
                        <Link to="/">
                            <LogoFull className="mx-auto h-10 w-auto" />
                        </Link>
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                        <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                            Email change confirmed
                        </h1>

                        <div className="mb-6 rounded-md border border-green-200 bg-green-50 p-4 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
                            <p className="font-medium">Almost done!</p>
                            <p className="mt-1 text-sm">
                                We've sent a verification email to your new email address. Please check your inbox and click the link to complete the change.
                            </p>
                        </div>

                        <Link to="/profile" className="block">
                            <Button type="button" variant="secondary" className="w-full" size="lg">
                                Back to profile
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 p-8 dark:bg-dark">
            <div className="w-full max-w-md">
                <div className="mb-8 text-center">
                    <Link to="/">
                        <LogoFull className="mx-auto h-10 w-auto" />
                    </Link>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-8 shadow-xs dark:border-gray-700 dark:bg-secondary">
                    <h1 className="mb-6 text-center text-2xl font-bold text-gray-900 dark:text-white">
                        Confirmation failed
                    </h1>

                    <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                        {errorMessage}
                    </div>

                    <Link to="/profile" className="block">
                        <Button type="button" className="w-full" size="lg">
                            Back to profile
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}